import React, { useCallback, useContext, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { NAVIGATION_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import { SECTION_ITEM_APPS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_APPS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { NavItemsContext } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import { ExploreMoreApps } from '../../../common/ui/explore-more-apps/ExploreMoreApps.tsx';
import { Nav4AppsContent } from '../../../common/ui/content/Nav4AppsContent.tsx';
import type { Nav4AppsContent_navigationCustomisation$key } from '../../../common/ui/content/__generated__/Nav4AppsContent_navigationCustomisation.graphql';
import type {
	Nav4AppsForLanding$key,
	Nav4AppsForLanding$data,
} from './__generated__/Nav4AppsForLanding.graphql';

type Nav4AppsForLandingProps = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	isExpanded: boolean;
	queryRef: Nav4AppsForLanding$key;
	navigationCustomisationFragment?: Nav4AppsContent_navigationCustomisation$key | null;
};

export function Nav4AppsForLanding(props: Nav4AppsForLandingProps): React.JSX.Element {
	const { queryRef, navigationCustomisationFragment } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { moreMenuIds } = useContext(NavItemsContext);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.APPS);
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<Nav4AppsForLanding$key>(
		graphql`
			fragment Nav4AppsForLanding on JiraQuery {
				...Nav4AppsContent
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);
	const menuId = MENU_ID_APPS;

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				const event = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
				});
				const isInMore = moreMenuIds.includes(L1_MENU_ID.APPS);
				fireUIAnalytics(event, SECTION_ITEM_APPS, { isInMore });
			}
		},
		[createAnalyticsEvent, moreMenuIds],
	);

	return (
		<Nav4AppsForLandingInner
			{...props}
			formattedMessage={formattedMessage}
			handleExpansionToggle={handleExpansionToggle}
			icon={icon}
			menuId={menuId}
			queryRef={data}
			navigationCustomisationFragment={navigationCustomisationFragment}
		/>
	);
}

const Nav4AppsForLandingInner = componentWithFG(
	'navx-global-side-nav',
	Nav4AppsForLandingInnerGSN,
	Nav4AppsForLandingInnerNav4,
);

type InnerProps = Omit<Nav4AppsForLandingProps, 'queryRef'> & {
	formattedMessage: string;
	handleExpansionToggle: (hasExpanded: boolean) => void;
	icon: JSX.Element;
	menuId: string;
	queryRef: Nav4AppsForLanding$data;
	navigationCustomisationFragment?: Nav4AppsContent_navigationCustomisation$key | null;
};

function Nav4AppsForLandingInnerGSN(props: InnerProps) {
	const {
		actionsOnHover,
		draggableRef,
		dropIndicator,
		formattedMessage,
		handleExpansionToggle,
		hasDragIndicator,
		icon,
		isDragging,
		isExpanded,
		menuId,
		onExpandFlyoutToggle,
		queryRef,
		navigationCustomisationFragment,
		visualContentRef,
	} = props;

	const handleExpansionToggle2 = useCallback(
		(hasExpanded: boolean) => {
			handleExpansionToggle(hasExpanded);
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[onExpandFlyoutToggle, handleExpansionToggle],
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle2}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-apps-landing"
				isDragging={isDragging}
				ref={draggableRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<UFOSegment name="nav4-sidebar-apps-landing">
					{/* This component uses the fragment from the ancestor query. */}
					<Nav4AppsContent
						queryRef={queryRef}
						navigationCustomisationFragment={navigationCustomisationFragment}
					/>
					<ExploreMoreApps />
				</UFOSegment>
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function Nav4AppsForLandingInnerNav4(props: InnerProps) {
	const {
		actionsOnHover,
		formattedMessage,
		handleExpansionToggle,
		icon,
		isExpanded,
		menuId,
		queryRef,
		navigationCustomisationFragment,
	} = props;

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				interactionName="nav4-sidebar-apps-landing"
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<UFOSegment name="nav4-sidebar-apps-landing">
					{/* This component uses the fragment from the ancestor query. */}
					<Nav4AppsContent
						queryRef={queryRef}
						navigationCustomisationFragment={navigationCustomisationFragment}
					/>
					<ExploreMoreApps />
				</UFOSegment>
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
